/**
 * Ikaros embroidery cart guard.
 *
 * Embroidery is priced as its own hidden product because Liquid cannot add
 * money to a line item. That makes every add-on a real, separate, and -
 * this is the point - customer-deletable line. The cart can therefore drift
 * out of step in two directions, and this file has to cover both.
 *
 * DIRECTION ONE - embroidery with no cap (order #6963)
 * A customer checked out with one line: Embroidery, Front single colour,
 * KD 4, and no cap. The brief was on the order; the cap was not. Handled
 * since then by deleting any embroidery line whose cap has gone.
 *
 * DIRECTION TWO - cap with no embroidery (order #7598)
 * The reverse, and it went unnoticed for longer because the cart looks
 * perfectly normal. The customer completes the studio, adds to cart, then
 * deletes the KD 4 embroidery line and leaves the cap. The cap still
 * carries Front thread, Side thread, Back thread - a complete stitching
 * brief - and they pay for a plain cap.
 *
 * #7598 arrived exactly so: Front style, Front thread, Side thread and
 * Back thread, no embroidery line, no charge. The workshop would have
 * stitched three placements for free.
 *
 * The old guard could not see this. It only ever asked whether an
 * embroidery line still had a cap, never whether a cap still had its
 * embroidery. Now it strips the stitching properties off any cap whose
 * add-ons have gone, so an unpaid cap reaches the workshop as what it
 * actually is: a plain cap.
 *
 * Stripping rather than re-adding is deliberate. Re-adding a line the
 * customer deliberately removed is hostile and would loop if they removed
 * it again. Removing the instructions they did not pay for is honest, and
 * the notice tells them why.
 *
 * HOW LINES ARE PAIRED
 * At add time the studio stamps the same _emb_group on the cap line and on
 * every add-on created with it. Underscore-prefixed properties are hidden
 * from the customer and from the order's customAttributes in the Admin API
 * - worth knowing, because their absence from an order view is not
 * evidence they were missing from the cart.
 *
 * THE ADD-IN-PROGRESS FLAG - do not remove
 * A legitimate add is two requests: add-ons first, cap a moment later.
 * Between them the cart genuinely holds an embroidery line with no cap.
 * An early build swept it mid-add and every embroidery order silently
 * became a plain cap. The studio raises window.__ikarosEmbAdding for the
 * length of an add and the sweep stands down while it is set.
 *
 * WHAT THIS CANNOT DO
 * Theme code cannot reach Shopify's checkout. Someone who edits the cart
 * through /cart/change.js directly, or who is already inside checkout when
 * the cart changes, is not covered. Closing that needs a Cart and Checkout
 * Validation Function, which is a Shopify app, not a theme file.
 */

(() => {
  const EMB_TYPE = 'Embroidery';
  const GROUP = '_emb_group';

  /* Properties the studio writes onto the cap line. Anything here is a
     stitching instruction and must not survive without a paid add-on. */
  const STITCH_KEYS = [
    'Front', 'Side', 'Back',
    'Front style',
    'Front thread', 'Side thread', 'Back thread',
    'Logo', 'WhatsApp',
    GROUP,
  ];

  let running = false;
  let lastSignature = '';

  const isEmb = (item) => (item.product_type || '') === EMB_TYPE;
  const groupOf = (item) => (item.properties && item.properties[GROUP]) || null;
  const addInFlight = () => !!window.__ikarosEmbAdding;

  const hasStitching = (item) =>
    !!item.properties && STITCH_KEYS.some((k) => k !== GROUP && item.properties[k]);

  /* Embroidery lines with no cap to belong to. Line numbers are 1-based,
     highest first, so removing one does not shift the next. */
  function orphanEmbroidery(items) {
    const capGroups = new Set();
    let capCount = 0;

    items.forEach((item) => {
      if (isEmb(item)) return;
      capCount += 1;
      const g = groupOf(item);
      if (g) capGroups.add(g);
    });

    const doomed = [];
    items.forEach((item, i) => {
      if (!isEmb(item)) return;
      const g = groupOf(item);
      if (g) {
        if (!capGroups.has(g)) doomed.push(i + 1);
      } else if (capCount === 0) {
        doomed.push(i + 1);
      }
    });
    return doomed.reverse();
  }

  /* Caps still carrying a stitching brief whose paid add-ons have gone.
     Returns the items themselves - these are edited, not deleted. */
  function unpaidCaps(items) {
    const embGroups = new Set();
    items.forEach((item) => {
      if (!isEmb(item)) return;
      const g = groupOf(item);
      if (g) embGroups.add(g);
    });

    return items.filter((item) => {
      if (isEmb(item) || !hasStitching(item)) return false;
      const g = groupOf(item);
      /* No group at all means the line predates pairing. Left alone: with
         nothing to match against, guessing would risk stripping a paid
         order's brief, which is the worse mistake. */
      if (!g) return false;
      return !embGroups.has(g);
    });
  }

  async function stripStitching(item) {
    const kept = {};
    Object.keys(item.properties || {}).forEach((k) => {
      if (!STITCH_KEYS.includes(k)) kept[k] = item.properties[k];
    });
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, properties: kept }),
    });
  }

  async function sweep() {
    if (running || addInFlight()) return;
    running = true;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      if (!cart || !cart.items || !cart.items.length) {
        lastSignature = '';
        return;
      }

      /* Signature includes the group, so deleting an add-on changes it and
         the sweep does not skip the very edit it needs to react to. */
      const sig = cart.items.map((i) => i.key + ':' + i.quantity + ':' + (groupOf(i) || '')).join('|');
      if (sig === lastSignature) return;
      lastSignature = sig;

      const doomed = orphanEmbroidery(cart.items);
      const unpaid = unpaidCaps(cart.items);
      if (!doomed.length && !unpaid.length) return;

      // Re-check: an add may have started while the cart was being read.
      if (addInFlight()) {
        lastSignature = '';
        return;
      }

      for (const line of doomed) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity: 0 }),
        });
      }

      for (const item of unpaid) {
        await stripStitching(item);
      }

      lastSignature = '';
      notify(doomed.length, unpaid.length);

      /* Only the cart page shows stale lines afterwards. Elsewhere the
         drawer re-reads on open, so a reload would be an unexplained jump. */
      const onCartPage = /\/cart(\/|$|\?)/.test(window.location.pathname + window.location.search);
      if (onCartPage && document.body.dataset.embSweepReload !== 'done') {
        document.body.dataset.embSweepReload = 'done';
        window.location.reload();
      }
    } catch (e) {
      console.warn('[ikaros-embroidery-guard]', e);
    } finally {
      running = false;
    }
  }

  function notify(removed, stripped) {
    let msg = '';
    if (stripped) {
      msg = stripped === 1
        ? 'The embroidery charge was removed from your cart, so the stitching has been taken off that cap. Add embroidery again from the product page if you still want it.'
        : 'The embroidery charges were removed from your cart, so the stitching has been taken off those caps. Add embroidery again from the product page if you still want it.';
    } else if (removed) {
      msg = removed === 1
        ? 'Embroidery needs a cap, so it was removed from your cart.'
        : 'Embroidery needs a cap, so those lines were removed from your cart.';
    }
    if (!msg) return;
    try {
      sessionStorage.setItem('ikaros-emb-removed', msg);
    } catch (e) {
      /* private mode - the change still happened, only the message is lost */
    }
  }

  function showNotice() {
    let msg = null;
    try {
      msg = sessionStorage.getItem('ikaros-emb-removed');
      if (msg) sessionStorage.removeItem('ikaros-emb-removed');
    } catch (e) {
      return;
    }
    if (!msg) return;

    const host =
      document.querySelector('cart-items-component') ||
      document.querySelector('.cart-items') ||
      document.querySelector('#MainContent');
    if (!host) return;

    const p = document.createElement('p');
    p.className = 'emb-guard-notice';
    p.setAttribute('role', 'status');
    p.textContent = msg;
    host.prepend(p);
  }

  function start() {
    showNotice();
    sweep();

    /* Horizon re-renders the cart by morphing sections rather than loading
       a page, so there is no single event to hang this on. Watching cart
       requests catches every path: drawer, cart page, quick add. */
    const origFetch = window.fetch;
    window.fetch = function (...args) {
      const url = String((args[0] && args[0].url) || args[0] || '');
      const p = origFetch.apply(this, args);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(() => setTimeout(sweep, 1200)).catch(() => {});
      }
      return p;
    };

    document.addEventListener('cart:updated', () => setTimeout(sweep, 1200));
    document.addEventListener('cart:refresh', () => setTimeout(sweep, 1200));
    window.addEventListener('pageshow', () => setTimeout(sweep, 600));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
